import axios from 'axios';

// Determine base URL based on environment
// Check if we're on localhost, 127.0.0.1, or ngrok (development)
const hostname = window.location.hostname;
const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
const isNgrok = hostname.includes('ngrok-free.app') || hostname.includes('ngrok.io');

// Use local server when in development
// - If localhost: use 127.0.0.1:8000 (to match Django server)
// - If 127.0.0.1: use 127.0.0.1:8000
// - If ngrok: use the same ngrok URL (assuming Django serves both frontend and API)
// - Otherwise: use production API or environment variable
const getBaseURL = () => {
  if (process.env.VUE_APP_API_URL) {
    return process.env.VUE_APP_API_URL;
  }
  if (isLocalhost) {
    return 'http://127.0.0.1:8000/api/';  // Changed from localhost to 127.0.0.1
  }
  if (isNgrok) {
    // When accessed through ngrok, use the same origin for API calls
    // This assumes Django is serving both frontend and API through the same ngrok tunnel
    return `${window.location.protocol}//${window.location.hostname}/api/`;
  }
  // Production: use whatever domain the frontend is being served from
  return `${window.location.protocol}//${window.location.hostname}/api/`;
};

// Create an axios instance with default config
const apiClient = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  withCredentials: true
});

// Function to get CSRF token from cookies
const getCSRFToken = () => {
  const name = 'csrftoken';
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        return decodeURIComponent(cookie.substring(name.length + 1));
      }
    }
  }
  return null;
};

// Function to fetch CSRF token from the server
const fetchCSRFToken = async () => {
  try {
    // Use the same base URL logic as the apiClient
    let csrfURL;
    if (isLocalhost) {
      csrfURL = 'http://127.0.0.1:8000/api/auth/csrf/';  // Changed from localhost
    } else if (isNgrok) {
      csrfURL = `${window.location.protocol}//${window.location.hostname}/api/auth/csrf/`;
    } else {
      csrfURL = `${window.location.protocol}//${window.location.hostname}/api/auth/csrf/`;
    }
    const response = await axios.get(csrfURL, {
      withCredentials: true,
      timeout: 3000
    });
    return response.data.csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

// Request interceptor to add auth token and CSRF token to requests
apiClient.interceptors.request.use(async config => {
  // Add authentication token
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  
  // Add CSRF token for non-GET requests — only needed when a session token is present
  // (DRF token auth endpoints like /auth/register/ are CSRF-exempt by design)
  if (config.method !== 'get' && token) {
    // Try to get from cookie first
    let csrfToken = getCSRFToken();

    // If not available in cookie, fetch from server
    if (!csrfToken) {
      try {
        csrfToken = await fetchCSRFToken();
      } catch (err) {
        console.error('Could not fetch CSRF token:', err);
      }
    }

    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
  }

  // Job search can exceed 10s on large production datasets (heavy filters + COUNT).
  const u = typeof config.url === 'string' ? config.url : '';
  if (u.includes('/jobs/search/')) {
    const jobSearchMs = 60000;
    if (config.timeout == null || config.timeout < jobSearchMs) {
      config.timeout = jobSearchMs;
    }
  }
  // Filter preview can hit 200k-row table — give it more headroom than the default 10s.
  if (u === '/jobs/' || u.endsWith('/jobs/') || u.includes('/jobs/?')) {
    const jobsMs = 30000;
    if (config.timeout == null || config.timeout < jobsMs) {
      config.timeout = jobsMs;
    }
  }
  if (u.includes('/notifications/unread-count/')) {
    const unreadMs = 25000;
    if (config.timeout == null || config.timeout < unreadMs) {
      config.timeout = unreadMs;
    }
  }
  if (u.includes('/cover-letters/generate')) {
    const coverGenMs = 180000;
    if (config.timeout == null || config.timeout < coverGenMs) {
      config.timeout = coverGenMs;
    }
  }
  // Onboarding match preview scans the full open-jobs table and makes an AI call for reasons.
  if (u.includes('/onboarding/preview-matches/')) {
    const previewMatchesMs = 30000;
    if (config.timeout == null || config.timeout < previewMatchesMs) {
      config.timeout = previewMatchesMs;
    }
  }

  return config;
}, error => {
  return Promise.reject(error);
});

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error.response || error);
    
    // Handle session expiry (401 errors) — only clear the token when the 401
    // comes from an auth-specific endpoint. Public read-only endpoints (e.g. /jobs/)
    // also return 401 for expired tokens, but clearing the token there causes all
    // subsequent requests to fire unauthenticated, breaking the rest of the page.
    if (error.response && error.response.status === 401) {
      const url = error.config?.url || '';
      // Only clear the token on dedicated auth endpoints (login, token, logout).
      // Data endpoints like /jobseeker/profile/, /resumes/, etc. return 401 for
      // unauthenticated background/polling requests and should NOT trigger a
      // logout — doing so causes logged-in users to be silently kicked out.
      const isAuthEndpoint = (
        url.includes('/auth/login') ||
        url.includes('/auth/token') ||
        url.includes('/auth/logout') ||
        url.includes('/auth/google')
      );
      if (isAuthEndpoint && localStorage.getItem('token')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete apiClient.defaults.headers.common['Authorization'];
      }
    }
    
    // Handle 403 errors (CSRF token issues)
    if (error.response && error.response.status === 403) {
      // Try to fetch a new CSRF token for next request
      fetchCSRFToken().catch(err => console.error('Failed to refresh CSRF token:', err));
    }
    
    return Promise.reject(error);
  }
);

// Initialize by fetching CSRF token when the API client is first loaded
fetchCSRFToken().catch(err => console.error('Initial CSRF token fetch failed:', err));

export default apiClient; 